import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import RouteFallback from '@/components/common/RouteFallback'
import EcosystemNotFound from '@/components/common/EcosystemNotFound'
import Layout from '@/components/layout/Layout'
import usePageTracking from '@/hooks/usePageTracking'

const ArmoryPage = lazy(() => import('@/pages/ArmoryPage'))

const basename = import.meta.env.BASE_URL

function LegacyRedirect({ mode }: { mode: 'builder' | 'skills' | 'items' | 'consumables' | 'debuffs' | 'crafting' }) {
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  if (mode === 'skills') {
    // Translate legacy Skills page params -> namespaced Skills tab params.
    const map: Array<[string, string]> = [
      ['q', 's_q'],
      ['class', 's_class'],
      ['weapon', 's_weapon'],
      ['type', 's_type'],
      ['sort', 's_sort'],
      ['view', 's_view'],
      ['open', 's_open'],
    ]
    map.forEach(([from, to]) => {
      const value = params.get(from)
      if (!value) return
      params.set(to, value)
      params.delete(from)
    })
  }

  params.set('tab', mode)

  const query = params.toString()
  return <Navigate to={query ? `/?${query}` : '/'} replace />
}

function AppRoutes() {
  usePageTracking()

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route
          index
          element={
            <Suspense fallback={<RouteFallback />}>
              <ArmoryPage />
            </Suspense>
          }
        />

        {/* Legacy aliases (kept for deep links / old shares). */}
        <Route path="builder" element={<LegacyRedirect mode="builder" />} />
        <Route path="skills" element={<LegacyRedirect mode="skills" />} />
        <Route path="debuffs" element={<LegacyRedirect mode="debuffs" />} />
        <Route path="items" element={<LegacyRedirect mode="items" />} />
        <Route path="consumables" element={<LegacyRedirect mode="consumables" />} />
        <Route path="crafting" element={<LegacyRedirect mode="crafting" />} />

        {/* Deprecated routes: keep navigable but funnel into tabs. */}
        <Route path="home" element={<LegacyRedirect mode="builder" />} />
        <Route path="classes" element={<LegacyRedirect mode="builder" />} />
        <Route path="*" element={<EcosystemNotFound app="armory" />} />
      </Route>
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter basename={basename}>
      <AppRoutes />
    </BrowserRouter>
  )
}
